import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import asyncComponent from 'util/asyncComponent';
import * as a from 'actions';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import FeaturePageShell from 'components/shell/FeaturePageShell';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import Theme from './theme';
import General from './general';
import styles from './RecommendationSettings.module.scss';

export default ({ match }) => (
  <Switch>
    <Route exact path={`${match.url}`} component={() => (<Redirect to={`${match.url}/general`} />)} />
    <Route path={`${match.url}/:tabId`} component={Settings} />
    <Route component={asyncComponent(() => import('app/routes/404'))} />
  </Switch>
);

const Settings = ({ match, history }) => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const {
    settings: {
      isFetched,
      isUpdatingRecs,
      recsVisible,
      forDiff = {},
      recs = {},
    },
    app: { currentShop },
  } = useSelector(store => store);

  const [tabId, setTabId] = useState(match.params.tabId || 'general');
  useEffect(() => setTabId(match.params.tabId), [match.params.tabId]);

  const onDiscardSetting = () => dispatch(a.resetRecsSettings());
  const onSaveSetting = () => dispatch(a.saveRecsSettings(currentShop));
  const settingsBaseUrl = match.url.replace(/\/[^/]+$/, '');
  const changeTab = id => history.push(`${settingsBaseUrl}/${id}`);
  const tabs = [
    { id: 'general', label: localizeMessage('recommendations.settings.tabs.general') },
    { id: 'theme', label: localizeMessage('recommendations.settings.tabs.colors') },
  ];
  const settingsChanged = JSON.stringify(forDiff.recs || {}) !== JSON.stringify(recs || {});
  const visibleChanged = forDiff.recsVisible !== recsVisible?.value;
  const hasChanges = settingsChanged || visibleChanged;
  const activePanel = {
    general: <General />,
    theme: <Theme />,
  }[tabId];

  const buttons = [
    {
      text: localizeMessage('common.save'),
      action: onSaveSetting,
      color: 'primary',
      testId: 'recommendation-settings-save',
    },
    {
      text: localizeMessage('common.discard'),
      action: onDiscardSetting,
      color: 'secondary',
      testId: 'recommendation-settings-discard',
    },
  ];

  return (
    <FeaturePageShell
      title={localizeMessage('recommendations.settings.title')}
      description={localizeMessage('recommendations.settings.description')}
      width="wide"
      isLoading={!isFetched}
      actions={(
        <SaveActionBtnGrp
          isFetching={isUpdatingRecs}
          buttons={buttons}
          disabled={!hasChanges}
        />
      )}
    >
      <div className={styles.settingsSurface}>
        <div className={styles.tabs} role="tablist" aria-label={localizeMessage('recommendations.settings.tabs.ariaLabel')}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={tab.id === tabId}
              className={`${styles.tab} ${tab.id === tabId ? styles.tabActive : ''}`.trim()}
              onClick={() => changeTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.tabPanel} role="tabpanel">
          {activePanel || <Redirect to={`${settingsBaseUrl}/general`} />}
        </div>
      </div>
    </FeaturePageShell>
  );
};
